import "../businessAnalysis/businessAnalysis.css";
import "@src/components/compliance/compliance.css";
import "@src/components/appraisals/appraisals.css";
import { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Table, Tabs } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenantFeatureFlags } from "@src/hooks/useTenantFeatureFlags";
import { getAppraisalOverviewQuery, getAppraisalSchedulesQuery, getAppraisalsQuery } from "@src/util/query/appraisal.query";
import { AppraisalDrawer } from "@src/components/appraisals/appraisal.drawer";
import { StartAppraisalModal } from "@src/components/appraisals/startAppraisal.modal";
import { formatDate, formatScore } from "@src/components/appraisals/appraisalHelpers";
import { RepeatTag, reviewerText, StatusTag } from "@src/components/appraisals/appraisalStatus";
import { AppraisalSchedules } from "@src/components/appraisals/appraisalSchedules";
import { PageNotFound } from "@src/pages/unAuthroizedAccess/pageNotFound.page";
import { CaretDownFilled } from "@ant-design/icons";

const OPEN = ["awaitingOfficer", "awaitingReviewer", "awaitingSignOff"];

const STATUS_FILTERS = [
    { value: "open", label: "Open" },
    { value: "overdue", label: "Overdue" },
    { value: "unassigned", label: "No reviewer" },
    { value: "awaitingOfficer", label: "Self-assessment" },
    { value: "awaitingReviewer", label: "With reviewer" },
    { value: "awaitingSignOff", label: "Officer to sign" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "", label: "Everything" },
];

/**
 * Appraisals: "My reviews" for anyone who reviews (the chosen role, or picked by name),
 * "My appraisals" for the user's own, and - for roles with Appraisals access - everyone's
 * appraisals with the overview figures.
 */
export const AppraisalsPage = () => {
    const { appraisalsEnabled, loading: flagsLoading } = useTenantFeatureFlags();
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    const permission = authenticatedUserDetail?.permission?.Appraisals;
    const isManager = permission?.access !== false;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [tab, setTab] = useState(isManager ? "all" : "reviews");
    const [statusFilter, setStatusFilter] = useState("open");
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(searchParams.get("appraisal"));
    const [startOpen, setStartOpen] = useState(false);

    // deep link from a notification: /user/appraisals?appraisal=<id>
    useEffect(() => {
        const id = searchParams.get("appraisal");
        if (id) setOpenId(id);
    }, [searchParams]);

    const { data: reviewsData, loading: reviewsLoading, refetch: refetchReviews } = useQuery(getAppraisalsQuery, {
        variables: { filter: { scope: "mine" } },
        skip: !appraisalsEnabled,
        fetchPolicy: "network-only",
    });
    const { data: allData, loading: allLoading, refetch: refetchAll } = useQuery(getAppraisalsQuery, {
        variables: { filter: { scope: "all", status: statusFilter || undefined } },
        skip: !appraisalsEnabled || !isManager,
        fetchPolicy: "network-only",
    });
    const { data: overviewData, refetch: refetchOverview } = useQuery(getAppraisalOverviewQuery, {
        skip: !appraisalsEnabled || !isManager,
        fetchPolicy: "network-only",
    });
    const { data: schedulesData, loading: schedulesLoading, refetch: refetchSchedules } = useQuery(getAppraisalSchedulesQuery, {
        skip: !appraisalsEnabled || !isManager,
        fetchPolicy: "network-only",
    });
    // the user's own appraisals (office staff are appraised too)
    const { data: mineData, refetch: refetchMine } = useQuery(getAppraisalsQuery, {
        variables: { filter: { scope: "own" } },
        skip: !appraisalsEnabled,
        fetchPolicy: "network-only",
    });

    // waiting for me first, then the ones I've done
    const reviews = [...(reviewsData?.getAppraisals?.response || [])].sort((a, b) => Number(OPEN.includes(b.status)) - Number(OPEN.includes(a.status)));
    const openReviews = reviews.filter((row) => OPEN.includes(row.status)).length;
    const all = allData?.getAppraisals?.response || [];
    const mine = mineData?.getAppraisals?.response || [];
    const overview = overviewData?.getAppraisalOverview?.response;
    const schedules = schedulesData?.getAppraisalSchedules?.response || [];

    const refresh = () => {
        refetchReviews();
        if (isManager) { refetchAll(); refetchOverview(); refetchSchedules(); }
        refetchMine();
    };

    const filteredAll = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? all.filter((row) => `${row.employeeName} ${row.title} ${row.reviewerName}`.toLowerCase().includes(q)) : all;
    }, [all, search]);

    const openRow = (id) => {
        setOpenId(id);
        setSearchParams({ appraisal: id }, { replace: true });
    };
    const closeDrawer = () => {
        setOpenId(null);
        setSearchParams({}, { replace: true });
    };

    const columns = (withEmployee = true) => [
        withEmployee && { title: "Employee", dataIndex: "employeeName", render: (v, row) => <button className="cmp-link" onClick={() => openRow(row._id)}>{v}</button> },
        { title: "Appraisal", dataIndex: "title", render: (v, row) => <span>{withEmployee ? v : <button className="cmp-link" onClick={() => openRow(row._id)}>{v}</button>}<RepeatTag months={row.repeatsEveryMonths} /></span> },
        withEmployee && { title: "Reviewer", dataIndex: "reviewerName", render: (_, row) => reviewerText(row) },
        // who started it (a repeat: who set the repeat up)
        { title: "Started", dataIndex: "createdAt", render: (v, row) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{row.createdByName || "--"}</span></div> },
        { title: "Due", dataIndex: "dueDate", render: (v, row) => <span className={row.overdue ? "cmp-overdue" : ""}>{formatDate(v)}</span>, sorter: (a, b) => new Date(a.dueDate) - new Date(b.dueDate) },
        { title: "Status", dataIndex: "status", render: (_, row) => <StatusTag row={row} /> },
        { title: "Score", dataIndex: "overallRating", render: (_, row) => formatScore(row) },
        { title: "", dataIndex: "open", align: "right", render: (_, row) => <button className="sm-btn" onClick={() => openRow(row._id)}>Open</button> },
    ].filter(Boolean);

    if (!flagsLoading && !appraisalsEnabled) {
        return <PageNotFound />;
    }

    const tabs = [
        isManager && { key: "all", label: "All Appraisals" },
        isManager && { key: "scheduled", label: `Scheduled (${schedules.length})` },
        { key: "reviews", label: `My Reviews (${openReviews})` },
        mine.length > 0 && { key: "mine", label: "My Appraisals" },
    ].filter(Boolean);
    const activeTab = tabs.some((t) => t.key === tab) ? tab : tabs[0].key;

    return (
        <div style={{ padding: "25px 48px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "5px" }}>
                <div className="grid-title">
                    Appraisals
                    <span className="grid-subtitle">{isManager && overview ? ` - ${overview.open} open` : ` - ${openReviews} waiting for you`}</span>
                </div>
                {isManager && overview && (
                    <div className="ba-stat-strip">
                        <div className="ba-stat"><span className="ba-stat-num">{overview.awaitingReviewer}</span><span className="ba-stat-lbl">With reviewer</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{overview.awaitingOfficer + overview.awaitingSignOff}</span><span className="ba-stat-lbl">With officer</span></div>
                        <div className="ba-stat"><span className={`ba-stat-num${overview.overdue ? " cmp-overdue" : ""}`}>{overview.overdue}</span><span className="ba-stat-lbl">Overdue</span></div>
                        {overview.unassigned > 0 && <div className="ba-stat"><span className="ba-stat-num cmp-overdue">{overview.unassigned}</span><span className="ba-stat-lbl">No reviewer</span></div>}
                        <div className="ba-stat"><span className="ba-stat-num">{overview.dueNext30Days}</span><span className="ba-stat-lbl">Due in 30 days</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{overview.completedThisYear}</span><span className="ba-stat-lbl">Done this year</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{overview.averageScorePercent != null ? `${overview.averageScorePercent}%` : "-"}</span><span className="ba-stat-lbl">Average score</span></div>
                    </div>
                )}
            </div>

            <div className="setting-body-inner grid-tabs" style={{ padding: "10px 0px" }}>
                <Tabs activeKey={activeTab} onChange={setTab} items={tabs} />
            </div>

            {activeTab === "all" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <div style={{ display: "flex", gap: 10 }}>
                            <Input
                                placeholder="Search employees, appraisals, reviewers..."
                                className="generic-input-control"
                                // style={{ width: 300 }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                suffix={<FontAwesomeIcon icon={faSearch} style={{ color: "#0091ae" }} />}
                            />
                            <Select className="custom-select" suffixIcon={<CaretDownFilled style={{marginBottom:'15px'}}/>} style={{ width: 300, marginTop:'0px' }} value={statusFilter} onChange={setStatusFilter} options={STATUS_FILTERS} />
                        </div>
                        {permission?.create !== false && <Button className="grid-filed-btn" onClick={() => setStartOpen(true)}>Start appraisal</Button>}
                    </div>
                    <Table
                        className="curvedTable"
                        loading={allLoading}
                        columns={columns(true)}
                        dataSource={filteredAll.map((row) => ({ ...row, key: row._id }))}
                        pagination={{ pageSize: 25, hideOnSinglePage: true }}
                        locale={{ emptyText: statusFilter === "open" ? "No open appraisals. Start one here or from an employee's profile - once, or repeating." : "Nothing matches." }}
                    />
                </>
            ) : activeTab === "scheduled" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <span className="cmp-hint">Appraisals set up to open on a later date, and ones that repeat. Each repeat counts from the day it first opened.</span>
                        {permission?.create !== false && <Button className="grid-filed-btn" onClick={() => setStartOpen(true)}>Start appraisal</Button>}
                    </div>
                    <AppraisalSchedules schedules={schedules} loading={schedulesLoading} showEmployee onChanged={refresh}
                        onOpenEmployee={(employeeId) => navigate(`/user/employee-detail/${employeeId}/tab/appraisals`)} />
                </>
            ) : activeTab === "mine" ? (
                <Table
                    className="curvedTable"
                    columns={columns(false)}
                    dataSource={mine.map((row) => ({ ...row, key: row._id }))}
                    pagination={{ pageSize: 25, hideOnSinglePage: true }}
                />
            ) : (
                <>
                    <div className="cmp-hint" style={{ marginTop: -10, marginBottom: 12 }}>
                        Appraisals you carry out. "Shared" ones are waiting for anyone with the reviewer role - open one and take it to fill it in.
                    </div>
                    <Table
                        className="curvedTable"
                        loading={reviewsLoading}
                        columns={columns(true)}
                        dataSource={reviews.map((row) => ({ ...row, key: row._id }))}
                        pagination={{ pageSize: 25, hideOnSinglePage: true }}
                        locale={{ emptyText: "Nothing waiting for you." }}
                    />
                </>
            )}

            <AppraisalDrawer visible={!!openId} appraisalId={openId} onClose={closeDrawer} onChanged={refresh} />
            <StartAppraisalModal visible={startOpen} onClose={() => setStartOpen(false)} onStarted={(res) => { refresh(); if (res?._id) openRow(res._id); }} />
        </div>
    );
};
